import {
    Application,
    Cleanroom,
    Collaborator,
    CollaboratorsList,
    Table,
    TableColumns,
    TemplateDefinition,
} from "../../common/types/cleanroom";
import { ApplicationConfig, DatasourceType } from "../../common/types/config";
import { NestedKeys } from "../../common/utils/Utils";
import { ValidationRules } from "../../common/managers/ValidationManager/ValidationManager";
import {
    APPLICATION_IDS,
    THIRD_PARTY_IDENTITY_PROVIDER,
} from "../../common/constants/constants";
import { ApplicationTable } from "./Step3/ApplicationInitialState";

export interface Options {
    label: string;
    value: string;
    category: string;
    isDisabled: boolean;
}

export type Step = {
    label: string;
    id: number;
    isVisible: boolean;
};

export type IdentityProvider_Collaborators = {
    id: string;
    name: string;
    email: string;
    type: string;
    status: string;
    share_back: string;
    snowflake_connector: {
        locator: string;
        region: string;
        csp: string;
    };
    identifier: string;
    account_identifier: string;
};

interface UserIdentifierSelection {
    label: string;
    selection_type: "SINGLE" | "MULTI";
    max_allowed_selections: number;
}

interface IdentifierType {
    overall_selection_type: "SINGLE" | "MULTI";
    single_valued_user_identifiers: string[];
    multi_valued_user_identifiers: string[];
    user_identifier_selections: UserIdentifierSelection[];
}

interface IdentifierTypes {
    PII: IdentifierType;
    "Hashed Email Only": IdentifierType;
    "Device ID": IdentifierType;
    CID: IdentifierType;
}

export type Errors = {
    [key: string]: string;
};

export interface Activation {
    id: string;
    name: string;
    description: string;
    icon: string;
    available: boolean;
    enabled?: number;
    active?: number;
    sub_text?: string;
}

export interface IdentityProvider {
    name: string;
    available: boolean;
    sub_text: string;
    id: THIRD_PARTY_IDENTITY_PROVIDER;
    icon: string;
    disabled: boolean;
    active: number;
    configuration: {
        role_options?: string[];
        identifier_types?: string[];
        identifier_types_object?: IdentifierTypes;
        collaborators?: IdentityProvider_Collaborators[];
    };
    banner?: {
        variant: "info" | "warning" | "error";
        message: string;
    };
}

export interface HasCollaborator {
    collaborator?: Collaborator;
}

export function isHasCollaborator(obj: any): obj is HasCollaborator {
    return obj && typeof obj === "object" && "collaborator" in obj;
}

export interface CleanroomCreateInstall {
    flow: { isEdit: boolean; isProvider: boolean };
    activeStep: number;
    cleanroom: Cleanroom & { collaborator?: Collaborator };
    draftCleanroom: Cleanroom & HasCollaborator;
    ui_state: {
        step1: {
            selectedDatasourceId: DatasourceType;
            selectedPreviewTable: string | null;
            disabledTables: Table[];
        };
        step2: {
            selectedIdentity: IdentityProvider | null;
            tablesAlreadyConsidered: Array<string>;
            initialJoins: Array<string>;
            removedInvalidRelations: boolean;
        };
        step3: {
            selectedAppId: ApplicationId | "";
        };
    };
    data: {
        availableObjects: TableColumSelector[];
        objectColumnsMapping: Record<string, string[]>;
        identityProviders: IdentityProvider[];
        collaboratorsList: CollaboratorsList[];
        templateDefinitions: TemplateDefinition[];
        availableActivations: Activation[];
        invalidObjects: Record<string, string[]> | {};
    };
    errors: Errors;
}

export enum CleanroomCreateInstallThunkActions {
    INITIALIZE_STATE = "cleanroomCreateInstall/initializeState",
    GET_CLEANROOM = "cleanroomCreateInstall/getCleanroom",
    CHECK_AND_UPDATE_DATASOURCE_ID_WITH_OBJECTS = "cleanroomCreateInstall/checkAndUpdateDatasourceIdWithObjects",
    SYNC_SNOWFLAKE_OBJECTS = "cleanroomCreateInstall/syncSnowflakeObjects",
    SET_CLEANROOM_TABLES_AND_FETCH_COLUMNS = "cleanroomCreateInstall/setCleanroomTablesAndFetchColumns",
    FETCH_COLUMNS_FOR_OBJECTS = "cleanroomCreateInstall/fetchColumnsForObjects",
    GET_AVAILABLE_IDENTITY_PROVIDERS = "cleanroomCreateInstall/getAvailableIdentityProviders",
    GET_IDENTITY_PROVIDERS = "cleanroomCreateInstall/getIdentityProviders",
    SET_ENRICHMENT_SIDE_EFFECTS = "cleanroomCreateInstall/setEnrichmentSideEffects",
    INITIALIZE_STEP4 = "cleanroomCreateInstall/initializeStep4",
    CHECK_AND_UPDATE_RUN_ANALYSIS_TOGGLE_FOR_CREATOR_AND_COLLABORATOR = "cleanroomCreateInstall/checkAndUpdateRunAnalysisToggleForCreatorAndCollaborator",
    GET_COLLABORATORS = "cleanroomCreateInstall/getCollaborators",
    HANDLE_FINISH_BTN = "cleanroomCreateInstall/handleFinishBtn",
    INITIATE_RELATIONS = "cleanroomCreateInstall/initiateRelations",
    ADD_RELATIONS_FOR_NEW_ENRICHED_COLUMNS = "cleanroomCreateInstall/addRelationsForNewEnrichedColumns",
    INITIALISE_AND_RESET_APPLICATION = "cleanroomCreateInstall/initialiseAndResetApplication",
    CHECK_AND_UPDATE_APPLICATION_TABLES = "cleanroomCreateInstall/checkAndUpdateApplicationTables",
    CHECK_AND_UPDATE_APPLICATION_CONFIGURATION = "cleanroomCreateInstall/checkAndUpdateApplicationConfiguration",
    FETCH_ACTIVATIONS = "cleanroomCreateInstall/fetchActivations",
    GET_TEMPLATE_DEFINITION = "cleanroomCreateInstall/getTemplateDefinition",
    CHECK_AND_UPDATE_APPLICATION_PRIVACY_SETTINGS = "cleanroomCreateInstall/checkAndUpdateApplicationPrivacySettings",
    HANDLE_NEXT_BTN = "cleanroomCreateInstall/handleNextBtn",
    HANDLE_JOIN_MODIFICATION_FOR_EDIT_MODE = "cleanroomCreateInstall/handleJoinModificationForEditMode",
    CAN_NAVIGATE_AWAY_FROM_APPLICATION = "cleanroomCreateInstall/canNavigateAwayFromApplication",
    REMOVE_RELATION_WRAPPER = "cleanroomCreateInstall/removeRelationWrapper",
    GET_INVALID_OBJECTS_IN_CLEANROOM = "cleanroomCreateInstall/getInvalidObjectsInCleanroom",
    HANDLE_INVALID_OBJECTS_FOR_EDIT_MODE = "cleanroomCreateInstall/handleInvalidObjectsForEditMode",
}
export enum CleanroomCreateInstall_Step3Actions {}

export type TableColumSelector = {
    fqtn: string;
    columns?: TableColumns[];
    object_uri?: string;
    source?: string;
    object_type?: string;
};

export type Field = {
    type: string;
    label: string;
    placeholder: string;
    show_label?: boolean;
    required?: boolean;
    infoMessage?: string;
    description?: string;
    singleSelect?: boolean;
};

export type FormConfig<
    T extends { configuration: Record<string, any> },
    OmittedKeys extends keyof T["configuration"] = never,
> = {
    TABLES: (Field & {
        name: "tables";
        tableType: ApplicationTable["table_type"];
    })[];
    COLUMNS: (Field & {
        name: Exclude<keyof T["configuration"], OmittedKeys>;
        isDisabled?: boolean;
    })[];
};

export type FieldComponentProps = Field & {
    value: any;
    options: { value: string; label: string }[];
    onChange: (e: any) => void;
    isDisabled?: boolean;
    footer?: any;
    footerMessage?: FooterMessage;
    formatOptionLabel?: (option: {
        value: string;
        label: string;
    }) => JSX.Element;
    errorMessage?: string;
    name: string;
    categoryDisplayNameMap?: Record<string, string>;
};

export type FooterMessage = { type?: string; content?: string };

export type FieldsToValidate<T> = {
    key: NestedKeys<T>;
    rules: ValidationRules[];
    overwriteErrorKey?: string;
}[];

export type ApplicationId =
    | `${APPLICATION_IDS}`
    | `${APPLICATION_IDS.CUSTOM_ANALYSIS}${string}`;

export type SelectOptionsWithCategory = {
    label: string;
    options: {
        label: string;
        value: string;
    }[];
};

export interface ApplicationCardConfig extends ApplicationConfig {
    application_id: ApplicationId;
    name: string;
    banner_text: string;
    banner_icon: string;
    icon?: string;
    description: string;
    is_ui_config_available: boolean;
}

export type EnrichmentConfig = {
    accountIdentifier: string;
    bootstrapDb: string;
};

export type GetValidationFields = (params: {
    application: Application;
    isProvider: boolean;
    isProviderRunEnabled?: boolean;
}) => {
    // @ts-ignore
    key: NestedKeys<Application>;
    rules: ValidationRules[];
}[];
